import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Form, Text } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'

import { observer } from 'mobx-react'

import ColorPicker from 'modules/core/components/Inputs/ColorPicker'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import useShiftConfigurations from 'modules/shift_management/hooks/useShiftConfigurations'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { SM_COLOR_CODES } from 'modules/shift_management/lib/colors'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackAddTemplateWeekShiftManagementSubmit } from 'modules/shift_management/lib/trackEvents'
import shiftConfigurations from 'modules/shift_management/store/api/collections/shiftConfigurations'
import type ShiftConfiguration from 'modules/shift_management/store/api/models/ShiftConfiguration'
import { DAY_TYPE } from 'modules/shift_management/store/api/models/ShiftConfiguration'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'
import { useBulkCudPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCudPlannedBreaks'

import ShiftFormComponent from './ShiftFormComponent'

interface Props {
  templateWeek: TemplateWeek
  shiftsByWeek: ShiftConfiguration[]
  shiftTemplateId: number
}

const WeekConfiguration = ({ templateWeek, shiftsByWeek, shiftTemplateId }: Props) => {
  const {
    shiftConfigForms,
    onAddShift,
    onDeleteShift,
    onCopyFirst,
    onAddBreak,
    onDeleteBreak,
    onAddRestDay,
    onAddEmptyCell,
  } = useShiftConfigurations(shiftsByWeek, templateWeek, shiftTemplateId)

  const { bulkCud } = useBulkCudPlannedBreaks()
  const templateWeekName = templateWeek.attributes.name
  const templateWeekColor = templateWeek.attributes.color
  const templateWeekId = templateWeek.attributes.id
  const { navigation } = useNavigation()
  const showColor = hasFeature('shift_management_colours_for_shifts')

  const navigate = useNavigate()

  const initialForm = useLazyRef<FactorialForm>(
    () =>
      new FactorialForm(
        {
          name: templateWeekName,
          color: templateWeekColor,
        },
        {
          name: 'string',
          color: 'string',
        }
      )
  ).current

  const shiftDays = shiftConfigForms.filter((form) => form.shiftConfig.isShift).length
  const restDays = shiftConfigForms.filter((form) => form.shiftConfig.isRestDay).length
  const emptyDays = shiftConfigForms.filter((form) => form.shiftConfig.isEmptyCell).length

  const { running, performAction } = useAsyncAction(async () => {
    const shifts = shiftConfigForms
      .filter(
        (form) =>
          form.shiftConfig.id ||
          form.shiftConfig.startAt ||
          form.shiftConfig.endAt ||
          form.shiftConfig.dayType === DAY_TYPE.restDay ||
          form.shiftConfig.dayType === DAY_TYPE.emptyCell
      )
      .map((shiftForm) => shiftForm.shiftConfig.data())
    await initialForm.save(templateWeek)
    await shiftConfigurations.rpc('bulk_cud', {
      template_week_id: templateWeekId,
      shift_configurations: shifts,
    })

    await shiftConfigurations.fetch({
      data: {
        template_weeks_ids: [templateWeekId],
      },
    })

    const shiftConfigIds = new Set(shiftConfigurations.map((c) => c.id))

    // ** Logic copied from Work Schedules**
    // When shift configurations are created, we assign the corresponding id to its form object.
    // This is necessary to send the right id for any break associated to that shift configuration,
    // which is required to create said break.
    //
    shiftConfigForms.forEach(({ shiftConfig }) => {
      shiftConfigurations.forEach((config) => {
        if (shiftConfig.isMatchWith(config)) {
          shiftConfig.setValues({ id: config.id })
        }
      })
    })

    const filteredShiftConfigs = shiftConfigForms.filter(({ shiftConfig }) =>
      shiftConfigIds.has(+shiftConfig.id)
    )

    const plannedBreaksParams = filteredShiftConfigs.map(({ shiftConfig, breaks }) => ({
      shiftConfigurationId: +shiftConfig.get('id').value,
      plannedBreaks: breaks.map((plannedBreak: PlannedBreakForm) => ({
        id: plannedBreak.id,
        startAt: plannedBreak.formattedStartAt,
        endAt: plannedBreak.formattedEndAt,
        duration: +plannedBreak.get('duration').value,
        breakType: plannedBreak.get('break_type').value,
        breakConfigurationId: +plannedBreak.get('break_configuration.id').value,
      })),
    }))

    // await this is necessary to avoid fetching the planned breaks before being created
    await bulkCud({ plannedBreaksParams })

    const formColor = initialForm.get('color').value
    const color = formColor ? nameFromHexCode(formColor) : null
    trackAddTemplateWeekShiftManagementSubmit({
      templateId: shiftTemplateId,
      weekId: templateWeek.id,
      workingDays: shiftDays - emptyDays,
      restDays: restDays,
      emptyDays: emptyDays,
      color: color,
    })

    navigate(
      navigation.shifts.templates.templatesShiftTemplateId.edit.fullPath({
        shiftTemplateId,
      })
    )
  })

  const onConfirm = () => {
    performAction()
  }

  const label = i18n.t('shift_management.rotating_shifts.schedules.name')

  return (
    <Form withBackground>
      <TextField
        label={label}
        value={initialForm.get('name').value}
        onChange={(newName) => initialForm.setValues({ name: newName })}
      />
      {showColor && (
        <>
          <Box flexDirection="row" alignItems="center">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('shift_management.add_shift.color_label')}
            </Text.Normal>
          </Box>
          <ColorPicker name="shiftColor" field={initialForm.get('color')} colors={SM_COLOR_CODES} />
        </>
      )}
      <ShiftFormComponent
        running={running}
        onConfirm={onConfirm}
        onAddShift={onAddShift}
        onDeleteShift={onDeleteShift}
        onAddBreak={onAddBreak}
        onDeleteBreak={onDeleteBreak}
        onAddRestDay={onAddRestDay}
        onAddEmptyCell={onAddEmptyCell}
        onCopyFirst={onCopyFirst}
        shiftConfigForms={shiftConfigForms}
      />
    </Form>
  )
}

export default observer(WeekConfiguration)
